import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { DataTable } from '../../components/DataTable';
import { businessDate, money } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { todayIn } from '../insights/lib/dateRange';
import {
  readStatement,
  useFoodpandaPayout,
  useFoodpandaPayouts,
  usePayoutActions,
  type FoodpandaPayout,
  type PayoutColumns,
  type PayoutPreview,
} from './api';

const STATUS: Record<
  FoodpandaPayout['status'],
  { label: string; color: 'default' | 'success' | 'warning' }
> = {
  DRAFT: { label: 'Draft', color: 'warning' },
  POSTED: { label: 'Posted', color: 'success' },
  VOIDED: { label: 'Voided', color: 'default' },
};

const COLUMN_LABELS: Record<keyof PayoutColumns, string> = {
  orderCode: 'Order number',
  gross: 'Order amount (gross)',
  commission: 'Commission',
  fees: 'Fees',
  net: 'Net paid',
};

/**
 * Foodpanda payouts: upload the payout statement, check each order against its invoice, then post
 * it (the invoices are paid, commission and fees are booked, the net lands in the bank).
 */
export function PayoutsTab() {
  const { format } = useShop();
  const payouts = useFoodpandaPayouts();
  const [importing, setImporting] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const rows = payouts.data?.data ?? [];
  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ alignItems: { sm: 'center' } }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Upload the payout statement Foodpanda sends (Excel or CSV). Each order is matched to its
          invoice; posting it pays the invoices and books Foodpanda’s commission and fees.
        </Typography>
        <Button
          variant="contained"
          startIcon={<UploadFileRounded />}
          onClick={() => setImporting(true)}
        >
          Import statement
        </Button>
      </Stack>
      <DataTable
        rows={rows}
        rowKey={(p) => p.id}
        loading={payouts.isLoading}
        onRowClick={(p) => setOpenId(p.id)}
        empty="No payouts yet. Import Foodpanda’s payout statement when it arrives."
        columns={[
          { key: 'ref', header: 'Statement', render: (p) => p.reference },
          { key: 'date', header: 'Paid on', render: (p) => businessDate(p.payoutDate, format) },
          {
            key: 'status',
            header: 'Status',
            render: (p) => (
              <Chip
                size="small"
                variant="outlined"
                label={STATUS[p.status].label}
                color={STATUS[p.status].color}
              />
            ),
          },
          {
            key: 'orders',
            header: 'Orders',
            hideBelow: 'sm',
            render: (p) => `${String(p.matched)} / ${String(p.lineCount)}`,
          },
          {
            key: 'gross',
            header: 'Gross',
            align: 'right',
            hideBelow: 'md',
            render: (p) => money(p.gross, format),
          },
          {
            key: 'kept',
            header: 'Commission + fees',
            align: 'right',
            hideBelow: 'md',
            render: (p) => money(String(Number(p.gross) - Number(p.net)), format),
          },
          { key: 'net', header: 'Net paid', align: 'right', render: (p) => money(p.net, format) },
        ]}
      />
      {importing && (
        <ImportDialog
          onClose={() => setImporting(false)}
          onSaved={(id) => {
            setImporting(false);
            setOpenId(id);
          }}
        />
      )}
      {openId && <PayoutDialog id={openId} onClose={() => setOpenId(null)} />}
    </Stack>
  );
}

function usePaymentAccounts() {
  return useQuery({
    queryKey: ['accounts'],
    queryFn: () => unwrap(api.GET('/accounts')),
    select: (d) => d.data.filter((a) => a.isPaymentAccount && a.isActive),
  });
}

function ImportDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const { format } = useShop();
  const notify = useNotify();
  const actions = usePayoutActions();
  const accounts = usePaymentAccounts();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PayoutPreview | null>(null);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [payoutDate, setPayoutDate] = useState(todayIn(format.timeZone));
  const [accountId, setAccountId] = useState('');

  const read = (f: File, columns: Partial<Record<keyof PayoutColumns, string>> = {}) => {
    setReading(true);
    setError(null);
    readStatement(f, columns)
      .then(
        (p) => {
          setPreview(p);
          if (!reference) setReference(f.name.replace(/\.[^.]+$/, '').slice(0, 100));
        },
        (e: unknown) => setError(e instanceof Error ? e.message : 'The file could not be read.'),
      )
      .finally(() => setReading(false));
  };
  const remap = (col: keyof PayoutColumns, header: string) => {
    if (!file || !preview) return;
    const columns = Object.fromEntries(
      Object.entries({ ...preview.columns, [col]: header || null }).flatMap(([k, v]) =>
        v ? [[k, v]] : [],
      ),
    );
    read(file, columns);
  };
  const bank =
    accounts.data?.find((a) => a.id === accountId) ?? accounts.data?.find((a) => a.code === '1010');
  const problems = preview?.lines.filter((l) => l.problem).length ?? 0;

  return (
    <Dialog open onClose={actions.create.isPending ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Import a Foodpanda payout statement</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <Button variant="outlined" loading={reading} onClick={() => input.current?.click()}>
              {file ? 'Choose another file' : 'Choose file'}
            </Button>
            <Typography variant="body2" color="text.secondary">
              {file?.name ?? '.xlsx or .csv'}
            </Typography>
            <input
              ref={input}
              type="file"
              hidden
              accept=".xlsx,.csv"
              data-testid="payout-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) {
                  setFile(f);
                  read(f);
                }
              }}
            />
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
          {preview && (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                }}
              >
                {(Object.keys(COLUMN_LABELS) as (keyof PayoutColumns)[]).map((col) => (
                  <TextField
                    key={col}
                    select
                    size="small"
                    label={COLUMN_LABELS[col]}
                    value={preview.columns[col] ?? ''}
                    onChange={(e) => remap(col, e.target.value)}
                  >
                    <MenuItem value="">
                      <em>{col === 'net' ? 'Work it out' : 'None'}</em>
                    </MenuItem>
                    {preview.headers.map((h) => (
                      <MenuItem key={h} value={h}>
                        {h}
                      </MenuItem>
                    ))}
                  </TextField>
                ))}
              </Box>
              <Totals totals={preview.totals} />
              {problems > 0 && (
                <Alert severity="warning">
                  {problems} of {preview.lines.length} lines will not settle an invoice exactly; see
                  below. Amounts without an invoice stay as Foodpanda credit.
                </Alert>
              )}
              {preview.skipped.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Left out of the payout:{' '}
                  {preview.skipped
                    .map((s) => `row ${String(s.row)} (${s.reason.toLowerCase()})`)
                    .join(', ')}
                </Typography>
              )}
              <LinesTable lines={preview.lines} />
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                }}
              >
                <TextField
                  label="Statement / payout id"
                  value={reference}
                  onChange={(e) => setReference(e.target.value.slice(0, 100))}
                  required
                />
                <TextField
                  label="Paid on"
                  type="date"
                  value={payoutDate}
                  onChange={(e) => setPayoutDate(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  select
                  label="Money arrived in"
                  value={bank?.id ?? ''}
                  onChange={(e) => setAccountId(e.target.value)}
                >
                  {(accounts.data ?? []).map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={actions.create.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={
            !preview?.lines.length || !reference.trim() || !payoutDate || actions.create.isPending
          }
          onClick={() =>
            actions.create
              .mutateAsync({
                reference: reference.trim(),
                payoutDate,
                ...(bank ? { accountId: bank.id } : {}),
                lines: (preview?.lines ?? []).map((l) => ({
                  orderCode: l.orderCode,
                  gross: l.gross,
                  commission: l.commission,
                  fees: l.fees,
                  // A negative net (Foodpanda took more than it paid) is worked out instead.
                  net: l.net.startsWith('-') ? undefined : l.net,
                })),
              })
              .then((p) => {
                notify.success('Payout saved as a draft: check it and post it');
                onSaved(p.id);
              }, notify.error)
          }
        >
          Save draft
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Totals({
  totals,
}: {
  totals: { gross: string; commission: string; fees: string; net: string };
}) {
  const { format } = useShop();
  const cells: [string, string][] = [
    ['Gross', totals.gross],
    ['Commission', totals.commission],
    ['Fees', totals.fees],
    ['Net paid', totals.net],
  ];
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
      }}
    >
      {cells.map(([label, v]) => (
        <Card key={label} variant="outlined">
          <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {money(v, format)}
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}

type Line = PayoutPreview['lines'][number] | NonNullable<FoodpandaPayout['lines']>[number];

function LinesTable({ lines }: { lines: Line[] }) {
  const { format } = useShop();
  return (
    <DataTable
      rows={lines}
      rowKey={(l) => String(l.lineNo)}
      empty="No order lines."
      columns={[
        { key: 'code', header: 'Order', render: (l) => `#${l.orderCode}` },
        {
          key: 'invoice',
          header: 'Invoice',
          render: (l) =>
            l.invoice ? (
              <Box>
                <Typography variant="body2">{l.invoice.number}</Typography>
                {Number(l.invoice.open) > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    unpaid {money(l.invoice.open, format)}
                  </Typography>
                )}
              </Box>
            ) : (
              '—'
            ),
        },
        { key: 'gross', header: 'Gross', align: 'right', render: (l) => money(l.gross, format) },
        {
          key: 'kept',
          header: 'Commission + fees',
          align: 'right',
          hideBelow: 'sm',
          render: (l) => money(String(Number(l.commission) + Number(l.fees)), format),
        },
        {
          key: 'net',
          header: 'Net',
          align: 'right',
          hideBelow: 'sm',
          render: (l) => money(l.net, format),
        },
        {
          key: 'check',
          header: 'Check',
          render: (l) =>
            l.problem ? (
              <Typography variant="caption" color="warning.main">
                {l.problem}
              </Typography>
            ) : (
              <Typography variant="caption" color="success.main">
                Matches
              </Typography>
            ),
        },
      ]}
    />
  );
}

function PayoutDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { format } = useShop();
  const { can } = useAuth();
  const notify = useNotify();
  const payout = useFoodpandaPayout(id);
  const actions = usePayoutActions();
  const [voiding, setVoiding] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reason, setReason] = useState('');
  const p = payout.data;
  const canPost = can('payments.create');
  const canVoid = can('payments.void');
  const busy = actions.post.isPending || actions.void.isPending || actions.remove.isPending;
  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Payout {p?.reference}
        {p && (
          <Chip
            size="small"
            variant="outlined"
            sx={{ ml: 1 }}
            label={STATUS[p.status].label}
            color={STATUS[p.status].color}
          />
        )}
      </DialogTitle>
      <DialogContent>
        {!p ? (
          <Typography color="text.secondary">Loading…</Typography>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Paid on {businessDate(p.payoutDate, format)} into {p.account.name}
              {p.payment ? ` · receipt ${p.payment.number ?? ''}` : ''}
            </Typography>
            <Totals totals={p} />
            {Number(p.adjustment) !== 0 && (
              <Alert severity="info">
                Foodpanda’s net differs from gross − commission − fees by{' '}
                {money(p.adjustment, format)}; it is booked with the fees.
              </Alert>
            )}
            {p.status === 'DRAFT' && (
              <Alert severity="info">
                Posting records a receipt of {money(p.gross, format)} from Foodpanda against the
                invoices, books {money(p.commission, format)} commission and{' '}
                {money(String(Number(p.gross) - Number(p.commission) - Number(p.net)), format)}{' '}
                fees, and {money(p.net, format)} into {p.account.name}.
              </Alert>
            )}
            <LinesTable lines={p.lines ?? []} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {p?.status === 'DRAFT' && (
          <Button color="error" disabled={busy} onClick={() => setDeleting(true)}>
            Delete draft
          </Button>
        )}
        {p?.status === 'POSTED' && canVoid && (
          <Button color="error" disabled={busy} onClick={() => setVoiding(true)}>
            Void
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
        {p?.status === 'DRAFT' && (
          <Button
            variant="contained"
            disabled={busy || !canPost}
            title={canPost ? undefined : 'Needs the permission to record payments'}
            onClick={() =>
              actions.post
                .mutateAsync({ id: p.id, version: p.version })
                .then(
                  () => notify.success('Payout posted: invoices paid, commission and fees booked'),
                  notify.error,
                )
            }
          >
            Post payout
          </Button>
        )}
      </DialogActions>
      <ConfirmDialog
        open={deleting}
        title="Delete this draft?"
        body="Nothing has been posted yet; you can import the statement again."
        confirmLabel="Delete"
        danger
        onClose={() => setDeleting(false)}
        onConfirm={() =>
          actions.remove.mutateAsync(id).then(() => {
            setDeleting(false);
            onClose();
          }, notify.error)
        }
      />
      {voiding && p && (
        <Dialog open onClose={() => setVoiding(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Void payout {p.reference}?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The receipt is voided (the invoices are unpaid again) and the commission and fees
              entry is reversed.
            </Typography>
            <TextField
              fullWidth
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVoiding(false)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              disabled={!reason.trim() || busy}
              onClick={() =>
                actions.void
                  .mutateAsync({ id: p.id, version: p.version, reason: reason.trim() })
                  .then(() => {
                    setVoiding(false);
                    notify.success('Payout voided');
                  }, notify.error)
              }
            >
              Void payout
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
}
